import React from 'react';
import { T } from '@tolgee/react';
import { TrackingOrder } from '../orderTracking.interface';
import { formatDate } from '../../../../utils/time.util';
import { Stack } from '../../../../assets/Stack';
import { BlueBox } from '../../../../assets/BlueBox';
import { TruckApprove } from '../../../../assets/TruckApprove';
import { TruckNotApprove } from '../../../../assets/TruckNotApprove';
import OrderInfoSection from './OrderInfoSection';
import RouteSection from './RouteSection';

interface TrackingHeaderProps {
	order: TrackingOrder;
}

interface HeaderChipProps {
	icon: React.ReactNode;
	label: React.ReactNode;
}

const HeaderChip: React.FC<HeaderChipProps> = ({ icon, label }) => (
	<div className="flex items-center gap-2 h-8 pl-2 pr-3 rounded-lg bg-track-hover text-[12.5px] font-semibold text-track-indigo whitespace-nowrap">
		<span className="flex flex-none">{icon}</span>
		<span>{label}</span>
	</div>
);

const TrackingHeader: React.FC<TrackingHeaderProps> = ({ order }) => {
	const isMultiple = order.deliveryMode === 'multiple';

	return (
		<section className="px-4 sm:px-7 pt-5 sm:pt-6 pb-6 border-0 border-b border-solid border-track-line flex flex-col gap-5">
			<div className="flex items-center justify-between gap-4 flex-wrap">
				<div className="flex flex-col gap-[2px] min-w-0">
					<div className="flex items-baseline gap-2 flex-wrap">
						<span className="text-base text-track-muted">
							<T keyName="orderTracking.orderId">Order ID:</T>
						</span>
						<span className="text-[20px] sm:text-[22px] leading-[1.25] font-bold tracking-[-0.01em] text-track-ink">
							#{order.orderNumber}
						</span>
					</div>
					<div className="flex gap-[6px] text-[13px]">
						<span className="text-track-muted">
							<T keyName="orderTracking.orderPlaced">Order placed:</T>
						</span>
						<span className="font-semibold text-track-ink">
							{formatDate(order.placedAt)}
						</span>
					</div>
				</div>

				<div className="flex items-center gap-2 flex-wrap">
					<HeaderChip
						icon={
							isMultiple ? (
								<Stack color="#484880" width={18} height={18} />
							) : (
								<BlueBox color="#484880" width={18} height={18} />
							)
						}
						label={
							isMultiple ? (
								<T keyName="orderTracking.multipleDelivery">
									Multiple delivery
								</T>
							) : (
								<T keyName="orderTracking.singleDelivery">Single delivery</T>
							)
						}
					/>
					<HeaderChip
						icon={
							order.transportIncluded ? (
								<TruckApprove color="#484880" width={18} height={18} />
							) : (
								<TruckNotApprove />
							)
						}
						label={
							order.transportIncluded ? (
								<T keyName="orderTracking.transportIncluded">
									Transport included
								</T>
							) : (
								<T keyName="orderTracking.transportExcluded">
									Transport excluded
								</T>
							)
						}
					/>
				</div>
			</div>

			<OrderInfoSection order={order} />

			<div className="border-0 border-t border-dashed border-track-line-strong" />

			<RouteSection
				pickup={order.pickup}
				delivery={order.delivery}
				deliveryMode={order.deliveryMode}
				trucks={order.trucks}
			/>
		</section>
	);
};

export default TrackingHeader;
